/**
 * JournalMapping — Review & Match step for the "Journal / GL Voucher" Doc Hub
 * category. Extracted Dr/Cr lines (from the AI/PDF table parser or the
 * CSV/Excel journal parser) each need a real GL account picked before this
 * can post through SP_Insert_JournalVoucher (same SP the manual Journal
 * Voucher screen uses — imported entries are indistinguishable from
 * hand-entered ones once posted).
 */
import React, { useState, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;

const money = v => (v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function JournalMapping({ extracted, mapping, setMapping, isDark, border, bgCard, text, textMut }) {
  const [accounts, setAccounts] = useState([]);
  const rows = extracted.lineItems || [];
  const [glSel, setGlSel] = useState(mapping.__glSel || rows.map(() => ''));
  const [narration, setNarration] = useState(mapping.__narration ?? (extracted.narration || ''));
  const [docDate, setDocDate] = useState(mapping.__docDate ?? new Date().toISOString().slice(0, 10));

  useEffect(() => {
    fetch(`${API}/api/vouchers/accounts/all`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json()).then(d => setAccounts(d.data || [])).catch(() => {});
  }, []);

  const totalDr = rows.reduce((s, r) => s + (r.debit || 0), 0);
  const totalCr = rows.reduce((s, r) => s + (r.credit || 0), 0);
  const balanced = rows.length >= 2 && totalDr === totalCr && totalDr > 0;
  const allMapped = rows.length > 0 && glSel.every(g => !!g);

  useEffect(() => {
    const items = rows.map((r, i) => {
      const acc = accounts.find(a => a.GLId === glSel[i]);
      return { ACId: glSel[i], AccountName: acc?.GLName || '', DrAmount: r.debit || 0, CrAmount: r.credit || 0, LineNarration: r.description || '' };
    });
    setMapping({
      __valid: balanced && allMapped,
      __glSel: glSel, __narration: narration, __docDate: docDate,
      summary: [`Voucher Date: ${docDate}`, `${items.length} line(s) · Dr ${money(totalDr)} = Cr ${money(totalCr)}`,
        !balanced ? 'Debit and Credit totals must match' : allMapped ? 'All lines mapped to GL accounts' : 'Every line needs a GL account'],
      savePayload: { DocDate: docDate, CommNarration: narration, items },
    });
  }, [glSel, narration, docDate, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>VOUCHER DATE</div>
          <input type="date" data-testid="staging-jv-date" value={docDate} onChange={e => setDocDate(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
        <div style={{ flex: 2 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: textMut, marginBottom: 6 }}>NARRATION</div>
          <input data-testid="staging-jv-narration" value={narration} onChange={e => setNarration(e.target.value)} placeholder="Narration for this journal entry..."
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${border}`, background: bgCard, color: text, fontSize: 12, boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: textMut }}>GL ACCOUNT MAPPING (Dr / Cr)</div>
        <div data-testid="staging-jv-balance-check" style={{ fontSize: 10, fontWeight: 700, color: balanced ? '#22c55e' : '#ef4444' }}>Dr {money(totalDr)} {balanced ? '=' : '≠'} Cr {money(totalCr)}</div>
      </div>
      <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'flex', padding: '7px 12px', background: isDark ? '#1a1a1a' : '#f8fafc', borderBottom: `1px solid ${border}` }}>
          {['Description', 'Debit', 'Credit', 'GL Account'].map((l, i) => <div key={l} style={{ flex: i === 3 ? 1.6 : i === 0 ? 2 : 1, fontSize: 9, fontWeight: 800, color: textMut, textTransform: 'uppercase' }}>{l}</div>)}
        </div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '9px 12px', borderTop: i > 0 ? `1px solid ${border}` : 'none' }}>
            <div style={{ flex: 2, fontSize: 11, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div>
            <div style={{ flex: 1, fontSize: 11, color: text, fontFamily: 'monospace' }}>{r.debit ? money(r.debit) : '—'}</div>
            <div style={{ flex: 1, fontSize: 11, color: text, fontFamily: 'monospace' }}>{r.credit ? money(r.credit) : '—'}</div>
            <div style={{ flex: 1.6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <select data-testid={`staging-jv-gl-select-${i}`} value={glSel[i] || ''} onChange={e => setGlSel(s => { const a = [...s]; a[i] = e.target.value; return a; })}
                style={{ flex: 1, padding: '5px 6px', borderRadius: 6, border: `1px solid ${glSel[i] ? '#22c55e' : border}`, background: bgCard, color: text, fontSize: 10 }}>
                <option value="">-- GL Account --</option>
                {accounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLName}</option>)}
              </select>
              {glSel[i] && <CheckCircle2 size={12} color="#22c55e" />}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div style={{ padding: 16, fontSize: 11, color: textMut, textAlign: 'center' }}>No Dr/Cr lines were extracted. Try the CSV/Excel method with Description, GL Code, Debit, Credit columns.</div>}
      </div>
    </div>
  );
}
